import {skipToken} from '@reduxjs/toolkit/query';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {
  type DataGridRow,
  type FilterValues,
  type OrderByRequestBody,
  useCreateDataQueryQuery,
  useGetDataByDataQueryQuery,
  useGetGridTotalRowsQuery,
} from '@dms/api/core';

import {CUSTOMER_INSIGHTS_REFRESH_VOLATILE_OPTIONS} from '../constants/CUSTOMER_INSIGHTS_REFRESH_VOLATILE_OPTIONS';

export interface CustomerInsightsGridQueryArgs {
  enabled: boolean;
  entitled: boolean;
  gridCode: string;
  filters: FilterValues;
  order: OrderByRequestBody[];
  columns: string[];
  rows: 'firstOne' | 'firstFive';
  includeTotal?: boolean;
}

export interface CustomerInsightsGridQueryResult {
  rows: DataGridRow[];
  total?: number;
  /** True only when `total` came from the count endpoint, not from loaded rows. */
  isTotalAuthoritative?: boolean;
  requestedRowsLimit: number;
  hasMore: boolean;
  /** True while more rows than the first page are loaded or already displayed. */
  isPaginationExpanded: boolean;
  isLoadingMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  isOmitted: boolean;
  loadMore: VoidFunction;
  showLess: VoidFunction;
  retry: VoidFunction;
}

const ROWS_OFFSET = 0;
const FIRST_ONE_ROW_LIMIT = 1;
const FIRST_FIVE_ROWS_LIMIT = 5;
const ADDITIONAL_ROWS_LIMIT = 10;

// eslint-disable-next-line complexity -- Pre-existing violation
export function useCustomerInsightsGridQuery(
  args: CustomerInsightsGridQueryArgs
): CustomerInsightsGridQueryResult {
  const paginationKey = JSON.stringify({
    gridCode: args.gridCode,
    filters: args.filters,
    order: args.order,
    columns: args.columns,
  });
  const [pagination, setPagination] = useState({
    key: paginationKey,
    rowsLimit: FIRST_FIVE_ROWS_LIMIT,
  });
  const expandedRowsLimit =
    pagination.key === paginationKey ? pagination.rowsLimit : FIRST_FIVE_ROWS_LIMIT;
  const isOmitted = !args.enabled || !args.entitled;
  const createQuery = useCreateDataQueryQuery(
    isOmitted
      ? skipToken
      : {
          code: args.gridCode,
          createDataQueryRequestBody: {
            filters: args.filters,
            order: args.order,
            smartSearch: null,
          },
        },
    CUSTOMER_INSIGHTS_REFRESH_VOLATILE_OPTIONS
  );
  // `data` remains populated with the last fulfilled response while RTK Query
  // is fetching a different argument. Only `currentData` belongs to the
  // current customer/filter combination.
  const dataQueryId = isOmitted ? undefined : createQuery.currentData?.dataQueryId;
  const shouldIncludeTotal = args.includeTotal !== false;
  const requestedRowsLimit = match(args.rows)
    .with('firstOne', () => FIRST_ONE_ROW_LIMIT)
    .with('firstFive', () => expandedRowsLimit)
    .exhaustive();
  const standardRowsArgument: Parameters<typeof useGetDataByDataQueryQuery>[0] = match({
    dataQueryId,
    rows: args.rows,
  })
    .with(
      {dataQueryId: Pattern.string, rows: Pattern.union('firstOne', 'firstFive')},
      ({dataQueryId: activeDataQueryId, rows}) => ({
        gridCode: args.gridCode,
        dataQueryId: activeDataQueryId,
        offset: ROWS_OFFSET,
        limit: rows === 'firstOne' ? FIRST_ONE_ROW_LIMIT : requestedRowsLimit,
        body: {columns: args.columns},
      })
    )
    .otherwise(() => skipToken);
  const totalRowsArgument: Parameters<typeof useGetGridTotalRowsQuery>[0] = match({
    dataQueryId,
    rows: args.rows,
    shouldIncludeTotal,
  })
    .with(
      {
        dataQueryId: Pattern.string,
        rows: Pattern.union('firstOne', 'firstFive'),
        shouldIncludeTotal: true,
      },
      ({dataQueryId: activeDataQueryId}) => ({
        code: args.gridCode,
        dataQueryId: activeDataQueryId,
      })
    )
    .otherwise(() => skipToken);
  const standardRowsQuery = useGetDataByDataQueryQuery(
    standardRowsArgument,
    CUSTOMER_INSIGHTS_REFRESH_VOLATILE_OPTIONS
  );
  const totalRowsQuery = useGetGridTotalRowsQuery(
    totalRowsArgument,
    CUSTOMER_INSIGHTS_REFRESH_VOLATILE_OPTIONS
  );
  const standardRowsStarted = standardRowsArgument !== skipToken;
  const totalRowsStarted = totalRowsArgument !== skipToken;
  // Growing the row limit swaps the RTK Query argument, which clears `currentData`
  // until the larger page arrives. Rendering that gap as a load would unmount the
  // section and silently collapse an expanded history group, so the last page
  // fulfilled for the same data query is carried over instead. The stored data
  // query id keeps rows of a previous customer or filter from ever being reused.
  const [lastFulfilledRows, setLastFulfilledRows] = useState<{
    dataQueryId: string;
    rows: DataGridRow[];
  }>();
  const fulfilledRows = standardRowsQuery.currentData?.rows;
  if (
    isNotNil(dataQueryId) &&
    isNotNil(fulfilledRows) &&
    lastFulfilledRows?.rows !== fulfilledRows
  ) {
    setLastFulfilledRows({dataQueryId, rows: fulfilledRows});
  }
  const carriedOverRows =
    lastFulfilledRows?.dataQueryId === dataQueryId ? lastFulfilledRows?.rows : undefined;
  const isCreatingDataQuery =
    !isOmitted &&
    (createQuery.isLoading || (createQuery.isFetching && createQuery.currentData === undefined));
  const isLoadingFirstRows =
    standardRowsStarted &&
    carriedOverRows === undefined &&
    (standardRowsQuery.isLoading ||
      (standardRowsQuery.isFetching && standardRowsQuery.currentData === undefined));
  // The count endpoint is consistently the slowest leg of the waterfall. Blocking
  // the whole section on it kept sections behind a skeleton long after their rows
  // had arrived, so it only blocks while there is nothing to render yet.
  const isLoadingBlockingTotal =
    totalRowsStarted &&
    shouldIncludeTotal &&
    carriedOverRows === undefined &&
    (totalRowsQuery.isLoading ||
      (totalRowsQuery.isFetching && totalRowsQuery.currentData === undefined));
  const isLoading =
    !isOmitted && (isCreatingDataQuery || isLoadingFirstRows || isLoadingBlockingTotal);
  const isFetching =
    !isOmitted &&
    !isLoading &&
    (createQuery.isFetching ||
      (standardRowsStarted && standardRowsQuery.isFetching) ||
      (totalRowsStarted && totalRowsQuery.isFetching));
  const isError =
    !isOmitted &&
    (createQuery.isError ||
      (standardRowsStarted && standardRowsQuery.isError) ||
      (totalRowsStarted && shouldIncludeTotal && totalRowsQuery.isError));
  const rows = match({dataQueryId, rows: args.rows})
    .with(
      {dataQueryId: Pattern.string, rows: Pattern.union('firstOne', 'firstFive')},
      () => standardRowsQuery.currentData?.rows ?? carriedOverRows ?? []
    )
    .otherwise(() => []);
  const total = match({
    shouldIncludeTotal,
    totalRowsStarted,
    isTotalError: totalRowsQuery.isError,
  })
    .with({shouldIncludeTotal: false}, () => rows.length)
    .with(
      {shouldIncludeTotal: true, totalRowsStarted: true, isTotalError: false},
      () => totalRowsQuery.currentData
    )
    .otherwise(() => undefined);
  const isTotalAuthoritative = shouldIncludeTotal && total !== undefined && !totalRowsQuery.isError;
  const hasMore = args.rows === 'firstFive' && total !== undefined && rows.length < total;
  const isPaginationExpanded =
    args.rows === 'firstFive' && requestedRowsLimit > FIRST_FIVE_ROWS_LIMIT;
  const isLoadingMore = isPaginationExpanded && standardRowsQuery.isFetching;
  const loadMore = () => {
    if (!hasMore) {
      return;
    }

    setPagination((currentPagination) => {
      const currentLimit =
        currentPagination.key === paginationKey
          ? currentPagination.rowsLimit
          : FIRST_FIVE_ROWS_LIMIT;

      return {
        key: paginationKey,
        rowsLimit: currentLimit + ADDITIONAL_ROWS_LIMIT,
      };
    });
  };
  const showLess = () => setPagination({key: paginationKey, rowsLimit: FIRST_FIVE_ROWS_LIMIT});
  const retry = () => {
    [
      !isOmitted && createQuery.isError ? createQuery.refetch : undefined,
      standardRowsStarted && standardRowsQuery.isError ? standardRowsQuery.refetch : undefined,
      totalRowsStarted && totalRowsQuery.isError ? totalRowsQuery.refetch : undefined,
    ]
      .filter(isNotNil)
      .forEach((refetch) => refetch());
  };

  return {
    rows,
    total,
    isTotalAuthoritative,
    requestedRowsLimit,
    hasMore,
    isPaginationExpanded,
    isLoadingMore,
    isLoading,
    isFetching,
    isError,
    isOmitted,
    loadMore,
    showLess,
    retry,
  };
}
